<template>
  <div id="userAddress" :class="['gradient-card', 'card', {'plain-card': ($utils.isPageForLicenses())}]">
    <div class="header">
      <div :class="panelHeaderIconClass"></div>
        <h4 class="title">{{panelHeaderTitle}}</h4>
      </div>

    <div class="body contact">
      <perfect-scrollbar class="auth-form address-form-wrapper cstm-ps-scrollbar-vertical">
        <el-form :model="addressForm" :rules="addressFormRules" ref="addressForm" class="address-form">
          <div class="form-col col-1">
            <div class="column-title">{{$t('label.companyInformation')}}</div>

            <!-- Company -->
            <el-form-item class="company f-item" prop="companyName">
              <el-input v-model="addressForm.companyName"
                :placeholder="`* ${$t('label.companyName')}`"
                class="cstm-input-white input-companyName"
                @focus="fieldEventCommonStyle('focus', 'input-companyName')"
                @blur="fieldEventCommonStyle('blur', 'input-companyName')"
              >
              </el-input>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_company">{{error}}</div>
            </el-form-item>

            <!-- VAT -->
            <el-form-item v-if="isAddressForCustomerBilling" class="vat f-item" prop="VAT">
              <el-input v-model="addressForm.VAT"
                :placeholder="`${$t('label.vat')}`"
                class="cstm-input-white">
              </el-input>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_vat">{{error}}</div>
            </el-form-item>

            <!-- Street and Number -->
            <el-form-item class="street f-item" prop="streetNumber">
              <el-input type="textarea" v-model="addressForm.streetNumber"
                :placeholder="`* ${$t('label.streetAndNumber')}`"
                @focus="fieldEventCommonStyle('focus', 'input-streetNumber')"
                @blur="fieldEventCommonStyle('blur', 'input-streetNumber')"
                class="cstm-input-white input-streetNumber leading-[1.5]">
              </el-input>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_street">{{error}}</div>
            </el-form-item>

            <!-- City -->
            <el-form-item class="f-item city" prop="city">
              <el-select
                v-model="addressForm.city"
                id="addressCustomCityAutoSearch"
                ref="addressCustomCityAutoSearch"
                class="cstm-selectbox-white"
                filterable
                remote
                value-key="placeId"
                :remote-method="debouncedSearch"
                :loading="dropdownCityLoader"
                :popper-class="'indigo-dp-popper address-city-autosearch-proper'"
                :placeholder="`* ${$t('label.city')}`"
                @change="handleCityDropdownEvent('changed', $event)"
                @focus="handleCityDropdownEvent('focus', $event)"
                @blur="handleCityDropdownEvent('blur', $event)"
                @input="handleCityDropdownTyping">

                <el-option
                  v-for="city in cityOptions"
                  :key="city.placeId"
                  :label="city.description"
                  :value="city">
                </el-option>
              </el-select>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_city">{{error}}</div>
            </el-form-item>

            <!-- Postal Code -->
            <el-form-item class="zip f-item" prop="zip">
              <el-input v-model="addressForm.zip"
                :placeholder="`* ${$t('label.postalCode')}`"
                @focus="fieldEventCommonStyle('focus', 'input-zip')"
                @blur="fieldEventCommonStyle('blur', 'input-zip')"
                class="cstm-input-white input-zip">
              </el-input>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_zip">{{error}}</div>
            </el-form-item>

            <!-- Country Code -->
            <el-form-item class="country-code f-item" prop="countryCode">
              <el-select v-model="addressForm.countryCode"
                filterable :placeholder="`* ${$t('label.country')}`"
                class="cstm-selectbox-white"
                :popper-class="'indigo-dp-popper country-states-autosearch-proper-class'"
                @focus="fieldEventCommonStyle('focus', 'input-country')"
                @blur="fieldEventCommonStyle('blur', 'input-country')"
                @change="handleStateDropdownVisibility">
                <el-option
                  v-for="(countryName, countryCode) in countries"
                  :key="countryCode"
                  :value-key="countryCode"
                  :label="countryName"
                  :value="countryCode">
                </el-option>
              </el-select>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_countryCode">{{error}}</div>
            </el-form-item>

            <!-- Country US States -->
            <el-form-item :class="{'show': addressForm.hasOwnProperty('countryCode') && showStateDropdown}" class="state f-item" prop="countryState">
              <el-select v-model="addressForm.state"
                filterable
                :placeholder="`* ${$t('label.state')}`"
                id="customCountryStateAutoSearch"
                ref="customCountryStateAutoSearch"
                class="cstm-selectbox-white"
                :popper-class="'indigo-dp-popper country-states-autosearch-proper-class'"
                @change="handleCountryStateDropdownEvent('changed', $event)"
                @focus="handleCountryStateDropdownEvent('focus', $event)"
                @blur="handleCountryStateDropdownEvent('blur', $event)"
                @input="handleCountryStateDropdownEvent('input', $event)">
                <el-option
                    v-for="state in usStates"
                    :key="state.abbreviation"
                    :label="state.name"
                    :value="state.abbreviation">
                  </el-option>
              </el-select>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_countryCode">{{error}}</div>
            </el-form-item>
          </div>

          <div class="form-col col-2">
            <div class="column-title">{{$t('label.accountCorrespondent')}}</div>

            <!-- First Name -->
            <el-form-item class="firstName f-item" prop="firstName">
              <el-input v-model="addressForm.firstName"
                :placeholder="`* ${$t('label.firstName')}`"
                @focus="fieldEventCommonStyle('focus', 'input-firstName')"
                @blur="fieldEventCommonStyle('blur', 'input-firstName')"
                class="cstm-input-white input-firstName">
              </el-input>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_firstName">{{error}}</div>
            </el-form-item>

            <!-- Last Name -->
            <el-form-item class="lastName f-item" prop="lastName">
              <el-input v-model="addressForm.lastName"
                :placeholder="`* ${$t('label.lastName')}`"
                @focus="fieldEventCommonStyle('focus', 'input-lastName')"
                @blur="fieldEventCommonStyle('blur', 'input-lastName')"
                class="cstm-input-white input-lastName">
              </el-input>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_lastName">{{error}}</div>
            </el-form-item>

            <!-- Email -->
            <el-form-item class="email f-item" prop="Email">
              <el-input v-model="addressForm.email"
                :placeholder="`* ${$t('label.email')}`"
                @focus="fieldEventCommonStyle('focus', 'input-email')"
                @blur="fieldEventCommonStyle('blur', 'input-email')"
                class="cstm-input-white input-email">
              </el-input>

              <div v-if="error.length > 0" class="animated shake backend-error-msg target-el-form-item_email">{{error}}</div>
            </el-form-item>

            <!-- Credit Card -->
            <div v-if="isAddressForCustomerBilling" class="credit-card-item f-item">
              <div class="column-title">{{$t('label.creditCard')}}</div>
              <div id="creditCardInfo">
                <div class="cc-info-content">
                  <div :class="['cc-logo', {
                    'mastercard' : (creditCardInfo.ccType === 'Mastercard'),
                    'visa' : (creditCardInfo.ccType === 'Visa')
                  }]"></div>
                  <div class="cc-number">
                    <template v-if="creditCardInfo.ccNumber">{{creditCardInfo.ccNumber}}</template>
                    <template v-else>{{'**** ****'}}</template>
                  </div>
                  <div class="cc-edit-icon" @click="handleCCIconClick"></div>
                </div>
                <div v-if="userInfo && userInfo.ccInfo.expm" class="cc-info-expiration">{{`${$t('label.expires')} ${creditCardInfo.ccExpiry}`}}</div>
              </div>
            </div>
          </div>
        </el-form>
      </perfect-scrollbar>

      <el-button @click="handleAddressFormSubmit" :disabled="currentRequestsFlag.length > 0" class="cstm-btn-white address-form-submit-btn">
        {{submitButtonLabel}}
      </el-button>
    </div>
  </div>
</template>

<script src="./user-address.js"></script>
<style lang="scss" src="./user-address.scss"></style>
